import { resolveAnalyticsConfig } from './config';
import { enrichProperties } from './enrichment';
import { createProvider } from './providers/createProvider';
import { NoopProvider } from './providers/NoopProvider';
import type {
    AnalyticsConfig,
    AnalyticsEventName,
    AnalyticsProperties,
    AnalyticsProvider,
    AnalyticsProviderName,
    AnalyticsProviderType,
    AnalyticsStatus,
    PageViewProperties,
    TrackArgs,
} from './types';
import {
    ENVIRONMENT,
    buildSafePageLocation,
} from './utils/runtimeMeta';
import { validateProperties } from './validation';

/**
 * Central analytics boundary. Components must not call GTM/GA directly.
 *
 * Single gate: REACT_APP_ANALYTICS_ENABLED
 * - disabled → analytics off (GTM not loaded)
 * - ready → enabled and provider initialized
 */
class AnalyticsService {
    private status: AnalyticsStatus = 'uninitialized';
    private enabled = false;
    private provider: AnalyticsProvider = new NoopProvider();
    private configuredProvider: AnalyticsProviderType = 'gtm';
    private gtmContainerId = 'GTM-KSZNW4C9';
    /** Injected provider for tests — takes precedence when set. */
    private injectedProvider: AnalyticsProvider | null = null;
    private lastPageViewPath: string | null = null;
    /** Privacy-safe current route (set by AnalyticsRouterListener). */
    private currentPath: string | null = null;
    private initPromise: Promise<void> | null = null;

    /** Validation / init warnings in development or when using the console provider. */
    private get isDebug(): boolean {
        return (
            ENVIRONMENT === 'development' ||
            this.configuredProvider === 'console'
        );
    }

    getStatus(): AnalyticsStatus {
        return this.status;
    }

    isReady(): boolean {
        return this.status === 'ready';
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    /** Active provider name (`noop` when disabled / failed). */
    getProviderName(): AnalyticsProviderName {
        return this.provider.name;
    }

    /**
     * Updates the current privacy-safe route for event enrichment.
     * Called from the router listener — not from page components.
     */
    setCurrentPath(path: string): void {
        this.currentPath = path || '/';
    }

    /**
     * Initialize from resolved config. Safe to call multiple times —
     * concurrent callers share the same promise.
     * After `disable()`, call `initialize()` again to turn analytics back on.
     */
    initialize(overrides: Partial<AnalyticsConfig> = {}): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }
        if (this.status === 'ready') {
            return Promise.resolve();
        }
        // Allow re-init after disable()
        if (this.status === 'disabled') {
            this.status = 'uninitialized';
        }

        this.status = 'initializing';
        this.initPromise = this.doInitialize(overrides).finally(() => {
            this.initPromise = null;
        });
        return this.initPromise;
    }

    private async doInitialize(
        overrides: Partial<AnalyticsConfig>
    ): Promise<void> {
        try {
            const config = resolveAnalyticsConfig(overrides);
            this.enabled = config.enabled;
            this.configuredProvider = config.provider;
            this.gtmContainerId = config.gtmContainerId;

            if (!this.enabled) {
                this.useNoopProvider();
                this.status = 'disabled';
                return;
            }

            await this.applyActiveProvider();
            this.status = 'ready';
        } catch (error) {
            if (this.isDebug) {
                // eslint-disable-next-line no-console
                console.error('[Analytics] Initialization failed', error);
            }
            this.useNoopProvider();
            this.status = 'disabled';
            this.enabled = false;
        }
    }

    disable(): void {
        this.enabled = false;
        this.useNoopProvider();
        this.lastPageViewPath = null;
        this.currentPath = null;
        this.status = 'disabled';
    }

    /**
     * Inject a provider (tests / advanced). Initializes the provider.
     * Pass null to clear injection and restore the configured provider.
     */
    async setProvider(provider: AnalyticsProvider | null): Promise<void> {
        this.injectedProvider = provider;
        if (provider) {
            await provider.initialize();
            this.provider = provider;
            this.status = this.enabled ? 'ready' : 'disabled';
            return;
        }
        if (this.enabled) {
            await this.applyActiveProvider();
            this.status = 'ready';
        } else {
            this.useNoopProvider();
            this.status = 'disabled';
        }
    }

    /** Reset service state (tests). */
    resetForTests(): void {
        this.status = 'uninitialized';
        this.enabled = false;
        this.useNoopProvider();
        this.configuredProvider = 'gtm';
        this.gtmContainerId = 'GTM-KSZNW4C9';
        this.injectedProvider = null;
        this.lastPageViewPath = null;
        this.currentPath = null;
        this.initPromise = null;
    }

    /** Typed events only — prefer this from app code.
     * @returns true if the event was delivered to the provider.
     */
    track<K extends AnalyticsEventName>(
        event: K,
        ...args: TrackArgs<K>
    ): boolean {
        return this.emit(event, (args[0] ?? {}) as AnalyticsProperties);
    }

    /**
     * Escape hatch for untyped event names. Prefer typed `track`.
     * Name is intentionally loud so call sites are easy to audit.
     * @returns true if the event was delivered to the provider.
     */
    unsafeTrack(event: string, properties?: AnalyticsProperties): boolean {
        return this.emit(event, properties ?? {});
    }

    /**
     * SPA screen view. Callers pass a privacy-safe path (see `toAnalyticsPagePath`).
     * Current path for `screen_name` enrichment is set via `setCurrentPath`.
     * Optional `extras` — e.g. error_code / error_message from the Error page.
     */
    pageView(path: string, extras: AnalyticsProperties = {}): void {
        if (!this.canSend()) {
            return;
        }

        const normalizedPath = path || '/';
        if (this.lastPageViewPath === normalizedPath) {
            return;
        }

        try {
            const pageLocation = buildSafePageLocation(normalizedPath);
            const payload: AnalyticsProperties = {
                ...(pageLocation ? { page_location: pageLocation } : {}),
                ...extras,
            };

            const validated = validateProperties(
                'screen_view',
                payload,
                this.isDebug
            );
            if (validated === null) {
                return;
            }

            const pageProps: Partial<PageViewProperties> & AnalyticsProperties =
                enrichProperties(validated, this.currentPath);

            this.provider.pageView(normalizedPath, pageProps);
            this.lastPageViewPath = normalizedPath;
        } catch {
            // Analytics must never break the app
        }
    }

    /** Validate → enrich → provider.track. Returns whether delivery succeeded. */
    private emit(event: string, properties: AnalyticsProperties): boolean {
        if (!this.canSend()) {
            return false;
        }

        try {
            const validated = validateProperties(
                event,
                properties,
                this.isDebug
            );
            if (validated === null) {
                return false;
            }
            this.provider.track(
                event,
                enrichProperties(validated, this.currentPath)
            );
            return true;
        } catch {
            // Analytics must never break the app — fail open for session flags
            return false;
        }
    }

    private useNoopProvider(): void {
        this.provider = new NoopProvider();
    }

    private canSend(): boolean {
        return this.status === 'ready';
    }

    private async applyActiveProvider(): Promise<void> {
        this.provider =
            this.injectedProvider ??
            createProvider(this.configuredProvider, this.gtmContainerId);
        await this.provider.initialize();
    }
}

export const analytics = new AnalyticsService();
